/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import mbpLogger from 'mbp-logger';
import {
    object, func, string, bool,
} from 'prop-types';
import { useUIDSeed } from 'react-uid';
import { useLazyQuery, useQuery } from '@apollo/client';
import { IconButton, makeStyles } from '@material-ui/core';

import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from '../../../utils/object';
import findContent from '../../../../../gql/queries/findContent';
import findProductsByPartNumbers from '../../../../../components/GraphqlComponents/SubscriptionPageV2/gql/findProductsByPartNumbers';
import ResponsiveImage from '../../../../../components/GraphqlComponents/GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import Variants from './Variants';
import { brandByCode, scrollToId } from '../helper';
import Loader from './Loader';
import SwapSubscriptionConfirmation from '../Dialogs/SwapSubscriptionConfirmation';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getSkuWithoutBrand } from '../../../../../helpers/common/helper';
import { getEnterpriseId } from '../../../../../../state/ducks/Member/ducks/EnterpriseId/EnterpriseId-Selectors';
import DynamicPrice from '../DynamicPrice';
import getPrice from '../../../../../helpers/getPrice/getPrice';

const useStyles = makeStyles({
    modalContainer: {
        '& .MuiPaper-root': {
            maxWidth: '715px !important',
            width: '100%',
            '@media screen and (max-width: 580px)': {
                maxWidth: '100% !important',
                height: '100%',
                maxHeight: 'fit-content',
                borderRadius: '0',
            },
        },
        '& .MuiDialog-paper': {
            margin: '20px',
            '@media screen and (max-width: 580px)': {
                margin: '0',
            },
        },
        '& hr': {
            borderTop: '1px solid #C4C4C4',
            margin: 0,
        },
    },
    headerContainer: {
        display: 'flex',
        alignItems: 'center',
        padding: '4px 0px 5px 19px',
        justifyContent: 'space-between',
        '& h1': {
            fontSize: '18px',
            lineHeight: '24px',
            fontWeight: '700',
        },
    },
    closeIconLabel: {
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    contentContainer: {
        padding: '16px 10px 5px',
        '& > div': {
            maxWidth: '505px',
            margin: '0 auto 17px',
            padding: '12px 10px',
            borderRadius: '4px',
            border: '1px solid #D9D9D9',
        },
    },
    image: {
        width: '120px',
        height: '100%',
        minHeight: '100%',
        maxWidth: '100%',
        marginRight: '20px',
        objectFit: 'cover',
        '@media screen and (max-width: 1024px)': {
            width: '98px',
            marginRight: '16px',
        },
    },
    recommendedSubscriptions: {
        display: 'flex',
        cursor: 'pointer',
        '& ._content': {
            fontSize: '18px',
            lineHeight: '26px',
            letterSpacing: '0.18px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            '@media screen and (max-width: 1024px)': {
                fontSize: '16px',
                lineHeight: '20px',
                letterSpacing: '0.14px',
            },
            '@media screen and (max-width: 425px)': {
                fontSize: '14px',
            },
            '@media screen and (max-width: 375px)': {
                fontSize: '12px',
            },
            '& > div:first-child > div:first-child': {
                fontWeight: '700',
            },
            '& button': {
                padding: '0',
                border: 'none',
                fontSize: '18px',
                lineHeight: '26px',
                boxShadow: 'none',
                textAlign: 'start',
                color: '#53237F',
                cursor: 'pointer',
                background: 'transparent',
                textDecoration: 'underline',
                '@media screen and (max-width: 1024px)': {
                    fontSize: '16px',
                    lineHeight: '24px',
                },
                '@media screen and (max-width: 425px)': {
                    fontSize: '14px',
                },
                '@media screen and (max-width: 375px)': {
                    fontSize: '12px',
                },
            },
        },
    },
});

const CancelButton = ({ handleClose, classes }) => (
    <IconButton
        aria-label="delete"
        align="right"
        data-test="checkout-recipient-form-close"
        data-testid="checkout-recipient-form-close"
        id="recipientFormCloseLink"
        classes={{
            label: classes.closeIconLabel,
        }}
        onClick={handleClose}
    >
        <svg xmlns="http://www.w3.org/2000/svg" width="39" height="39" viewBox="0 0 39 39" fill="none">
            <path d="M25.1819 11.6724L19.4977 17.3565L13.8136 11.6724L11.5811 13.9049L17.2652 19.589L11.5811 25.2732L13.8136 27.5057L19.4977 21.8215L25.1819 27.5057L27.4144 25.2732L21.7302 19.589L27.4144 13.9049L25.1819 11.6724Z" fill="black" />
        </svg>
    </IconButton>
);

const SwapSubscriptionUI = ({
    setConfirmationPage,
    setPayload,
    partNumbersLoading,
    partNumbersData,
    subscription,
    productPrice,
    handleClose,
    classes,
    copy,
    loading,
}) => {
    const [selectedProduct, setSelectedProduct] = useState({});
    const seed = useUIDSeed();
    const dispatch = useDispatch();
    const [productSkuPrices, setProductSkuPrices] = useState([]);
    const enterpriseId = useSelector(getEnterpriseId);
    const recipientZipCode = subscription?.recipient?.[0]?.zipCode || subscription?.recipient?.[0]?.zipcode;

    let data = partNumbersData?.findAvailProductSkusByPartNumbers ?? [];
    data = data.map((prod) => ({
        ...prod,
        productSkus: prod?.productSkus?.filter((sku) => getSkuWithoutBrand(sku?.id) !== subscription.productId) ?? [],
    }));
    const productSkuList = data?.map((_) => _.productSkus).flat(1) || [];
    const dynamicPriceInstance = new DynamicPrice(productSkuList, enterpriseId, true);
    const {
        getPriceRules, getRetailPrice, getSalePrice, getRetailDisplayPrice,
    } = dynamicPriceInstance;

    useEffect(() => {
        if (productSkuList.length > 0) {
            dynamicPriceInstance.call(true, recipientZipCode).then((productPrices) => {
                setProductSkuPrices(productPrices?.newPrices || []);
            }).catch(() => {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    component: 'Variants.js',
                    message: 'Failed to get dynamic prices on subscription V2 choose plan page',
                });
            });
        }
    }, [productSkuList.length]);

    const getImagePath = (image, type = 'c') => {
        let path = image?.path ? `${image?.path}${image?.name}${type}.jpg` : '';
        if (path.startsWith('//')) {
            path = `https:${path}`;
        }
        return path;
    };

    const swapSubscription = (newSubscriptionData) => {
        setConfirmationPage(true);
        setPayload(newSubscriptionData);
    };

    const selectProduct = (variant) => {
        setSelectedProduct(variant);
        setTimeout(() => scrollToId({ id: 'product_slider' }), 250);
    };

    const handleViewAndSelect = (product) => {
        selectProduct(product);
        dispatch(trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: 'click - Change Product',
            eventLabel: `View and Select - ${product.name}`,
        }));
    };

    const dynamicPriceList = (sku, skuPrices = null) => {
        let priceDetails = [];
        if (productSkuPrices?.length > 0 || skuPrices?.length > 0) {
            const skuItemPriceList = (skuPrices || productSkuPrices || []).find((skuItem) => skuItem.id === sku.id)?.prices || [];
            const currentPriceList = JSON.parse(JSON.stringify(sku?.prices || []));
            priceDetails = currentPriceList.map((price) => {
                /**
                 * Retail price update for standard products by dynamic price API, not updating for subscription products
                 */
                if (price.type === 'retail' && skuItemPriceList.length > 0 && getRetailPrice(skuItemPriceList)) {
                    const retailPrice = getRetailPrice(skuItemPriceList);
                    const salePrice =  getSalePrice(skuItemPriceList);
                    // eslint-disable-next-line no-param-reassign
                    price.value = retailPrice;
                    if ((retailPrice  === salePrice) ||  retailPrice > salePrice) {
                        // eslint-disable-next-line no-param-reassign
                        price.value = getRetailDisplayPrice(skuItemPriceList);
                    }
                }

                /**
                 * Used to get what would be the retail dynamic price to show as comparison to discounted subscription price
                 */
                if (price.type === 'retail' && skuItemPriceList.length > 0 && getRetailDisplayPrice(skuItemPriceList)) {
                // eslint-disable-next-line no-param-reassign
                    price.value = getRetailDisplayPrice(skuItemPriceList);
                }

                /**
                 * Sale price update for standard products and subscription products by dynamic price API
                 */
                if (price.type === 'sale' && skuItemPriceList.length > 0 && getSalePrice(skuItemPriceList)) {
                // eslint-disable-next-line no-param-reassign
                    price.value = getSalePrice(skuItemPriceList);
                }
                return price;
            });
        }
        const currentSkuRetailPrice = Number((priceDetails)?.find((price) => price.type === 'retail')?.value || 0);
        const currentSkuActualPriceDetails = getPrice((priceDetails));
        return [currentSkuRetailPrice, currentSkuActualPriceDetails];
    };

    function findProductRange(prices) {
        let minPrice = Infinity;
        let maxPrice = -Infinity;

        prices.forEach(([price, { hasSale, value }]) => {
            minPrice = Math.min(minPrice, hasSale ? value : price);
            maxPrice = Math.max(maxPrice, hasSale ? value : price);
        });

        return { min: minPrice, max: maxPrice };
    }

    const getPriceRange = (parentSku, productSkuPricess) => {
        const parentSkuWithoutP = parentSku?.replace('-P', '');
        const skuPrices = productSkuPricess.filter((sku) => sku.id.includes(parentSkuWithoutP)) || [];
        const dynamicSkuPrices = skuPrices.map((_) => dynamicPriceList(_));
        return findProductRange(dynamicSkuPrices);
    };

    return (
        <>
            <div className={classes.headerContainer}>
                <h1>CHANGE PRODUCT</h1>
                <CancelButton classes={classes} handleClose={handleClose} />
            </div>
            <hr />
            <div className={classes.contentContainer}>
                {(loading || partNumbersLoading || !productSkuPrices?.length > 0) && <Loader />}
                {isEmpty(data) && <h3 style={{ textAlign: 'center' }}>{copy?.non_availability_copy ? copy.non_availability_copy : 'No Swap Subscriptions Available'}</h3>}
                {/* recommended subscriptions */}
                {!partNumbersLoading && productSkuPrices?.length > 0 && data?.map((product) => {
                    const { min, max } = getPriceRange(product.partNumber, productSkuPrices);
                    return (
                        <React.Fragment key={seed(product)}>
                            {
                                !isEmpty(selectedProduct) && selectedProduct?.partNumber === product.partNumber ? (
                                    <Variants
                                        products={data}
                                        getImagePath={getImagePath}
                                        subscription={subscription}
                                        productPrice={productPrice}
                                        product={selectedProduct}
                                        productSkuPrices={productSkuPrices}
                                        closeVariants={setSelectedProduct}
                                        swapSubscription={swapSubscription}
                                        dynamicPriceList={dynamicPriceList}
                                        getPriceRules={getPriceRules}
                                    />
                                ) : (
                                    <>
                                        {product?.productSkus?.length > 0 && (
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={() => { }}
                                                className={classes.recommendedSubscriptions}
                                                onClick={() => handleViewAndSelect(product)}
                                            >
                                                <ResponsiveImage
                                                    className={classes.image}
                                                    path={getImagePath(product.image)}
                                                    alt={product.name}
                                                    params={{ desktop: 'quality=100', tablet: 'quality=100', mobile: 'quality=100' }}
                                                />
                                                <div className="_content">
                                                    <div>
                                                        <div>{product.name}</div>
                                                        {min === max ? <div>${max}</div> : <div>${min} - ${max}</div>}
                                                    </div>
                                                    <button type="button">View Price and Select</button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )
                            }
                        </React.Fragment>
                    );
                })}
            </div>
        </>
    );
};

const SubscriptionSwap = ({
    openModal, toggleModal, subscription, productPrice,
}) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const [payload, setPayload] = useState({});
    const [confirmationPage, setConfirmationPage] = useState(false);
    const brand = brandByCode[subscription.brand] || '1800flowers';
    const query = '\\"url\\": \\"/subscriptions\\"';
    const contentType = 'subscriptions_management';
    const SUBSCRIPTIONS_MANAGEMENT = findContent({ brand, contentType, query });

    const { data, loading, error } = useQuery(SUBSCRIPTIONS_MANAGEMENT, { variables: { brand } });

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            jsError: error,
            message: `Failed to fetch content data for: content type ${contentType}, query ${query}`,
        });
        return <div>Error! something went wrong, please try again later</div>;
    }

    const cmsData = data?.findContent?.content?.entries?.[0] || {};
    const products = cmsData?.recommended_subscriptions?.products || [];
    const confirmationCopy = cmsData?.swap_confirmation_copy || {};
    const partNumbers = products.map((product) => product.partnumber);

    const PRODUCT_BY_PART = findProductsByPartNumbers(brand, partNumbers, null);
    const [getProductDetails, { loading: partNumbersLoading, error: partNumbersError, data: partNumbersData }] = useLazyQuery(PRODUCT_BY_PART);

    if (partNumbersError) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            jsError: error,
            message: `Failed to fetch content data for: content type ${contentType}, query ${query}`,
        });
        return <div>Error! something went wrong, please try again later</div>;
    }

    const handleClose = () => {
        dispatch(trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: 'click - Change Product',
            eventLabel: 'Close Modal',
        }));
        toggleModal();
    };

    useEffect(() => {
        if (partNumbers.length > 0) getProductDetails();
    }, [cmsData]);

    return (
        <Dialog
            onClose={handleClose}
            open={openModal}
            className={classes.modalContainer}
        >
            {confirmationPage ? (
                <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <CancelButton classes={classes} handleClose={handleClose} />
                    </div>
                    <SwapSubscriptionConfirmation
                        data={payload}
                        toggle={toggleModal}
                        copy={confirmationCopy}
                        setPayload={setPayload}
                        subscription={subscription}
                        close={() => setConfirmationPage(false)}
                    />
                </>
            ) : (
                <SwapSubscriptionUI
                    classes={classes}
                    loading={loading}
                    copy={confirmationCopy}
                    setPayload={setPayload}
                    handleClose={handleClose}
                    productPrice={productPrice}
                    subscription={subscription}
                    partNumbersData={partNumbersData}
                    partNumbersLoading={partNumbersLoading}
                    setConfirmationPage={setConfirmationPage}
                />
            )}
        </Dialog>
    );
};

CancelButton.propTypes = {
    classes: object.isRequired,
    handleClose: func.isRequired,
};

SwapSubscriptionUI.propTypes = {
    copy: object.isRequired,
    classes: object.isRequired,
    loading: bool.isRequired,
    partNumbersLoading: bool.isRequired,
    partNumbersData: object.isRequired,
    setConfirmationPage: func.isRequired,
    setPayload: func.isRequired,
    subscription: object.isRequired,
    productPrice: string.isRequired,
    handleClose: func.isRequired,
};

SubscriptionSwap.propTypes = {
    toggleModal: func.isRequired,
    openModal: bool.isRequired,
    subscription: object.isRequired,
    productPrice: string.isRequired,
};

export default SubscriptionSwap;
